import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type RelationType,
  type SetActingPersonRelationCommandResult,
  putPartiesCompanyPartyPublicIDActingPerson,
} from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { PARTY_QUERY_KEY } from "Api/Queries/Party/usePartyQuery";

type Props = {
  onSuccess?: (result: SetActingPersonRelationCommandResult) => void;
  onError?: () => void;
};

/**
 * Links the signed-in person to their company as its acting person. The person is resolved on the
 * server from the caller's Identity, so no Party is created here.
 */
export const useSetActingPersonMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({
        companyPartyPublicID,
        relationType,
      }: {
        companyPartyPublicID: string;
        relationType: RelationType;
      }) =>
        putPartiesCompanyPartyPublicIDActingPerson(
          { relationType },
          companyPartyPublicID,
        ),
    ),
    onSuccess: (result: SetActingPersonRelationCommandResult) => {
      // The company's relation list and the linked person both change.
      queryClient.invalidateQueries({ queryKey: [PARTY_QUERY_KEY] });
      onSuccess?.(result);
    },
    onError,
  });
};
